import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Mail, Phone, MapPin, Send } from 'lucide-react'
import { motion } from 'framer-motion'

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    subject: '',
    body: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  }

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, delay: 0.2 },
    },
  }

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, delay: 0.4 },
    },
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      // Create mailto link with form data
      const subject = encodeURIComponent(formData.subject)
      const body = encodeURIComponent(
        `Name: ${formData.fullName}\nEmail: ${formData.email}\n\nMessage:\n${formData.body}`
      )
      const mailtoLink = `mailto:info@pexipay.com?subject=${subject}&body=${body}`

      // Open email client
      window.location.href = mailtoLink

      setSubmitStatus('success')
      setFormData({ fullName: '', email: '', subject: '', body: '' })
    } catch (error) {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <motion.div
      className="min-h-screen bg-gray-900"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute rounded-full -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200/30 to-blue-200/30 blur-3xl animate-pulse-slow"></div>
        <div
          className="absolute rounded-full -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-200/30 to-orange-200/30 blur-3xl animate-pulse-slow"
          style={{ animationDelay: '1s' }}
        ></div>
        <div className="absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full top-1/2 left-1/2 w-96 h-96 bg-gradient-to-r from-orange-100/20 to-blue-100/20 blur-3xl animate-float"></div>
      </div>

      <div className="relative z-10 max-w-6xl px-4 py-8 mx-auto sm:px-6 lg:px-8">
        {/* Back Navigation */}
        <motion.div className="mb-8" variants={fadeInUp}>
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-orange-400 transition-colors hover:text-orange-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div className="mb-12 text-center" variants={fadeInUp}>
          <h1 className="mb-4 text-4xl font-bold text-white">Contact Us</h1>
          <p className="max-w-3xl mx-auto text-xl text-gray-300">
            Have questions about our cryptocurrency payment solutions? We're here to help you
            integrate seamless payment processing into your business.
          </p>
        </motion.div>

        <motion.div className="grid gap-12 lg:grid-cols-2" variants={staggerContainer}>
          {/* Contact Form */}
          <motion.div
            className="p-8 border bg-slate-800/50 backdrop-blur-sm rounded-2xl border-slate-700/50"
            variants={fadeInLeft}
          >
            <h2 className="mb-6 text-2xl font-semibold text-white">Send us a Message</h2>

            {submitStatus === 'success' && (
              <div className="p-4 mb-6 border rounded-lg bg-green-500/20 border-green-500/50">
                <p className="text-green-400">
                  Your email client should now open with the message pre-filled. Send the email to
                  complete your inquiry.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 mb-6 border rounded-lg bg-red-500/20 border-red-500/50">
                <p className="text-red-400">
                  There was an error processing your request. Please try again or contact us
                  directly.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="fullName" className="block mb-2 text-sm font-medium text-gray-300">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 text-white placeholder-gray-400 transition-colors border rounded-lg bg-slate-700/50 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-300">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 text-white placeholder-gray-400 transition-colors border rounded-lg bg-slate-700/50 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block mb-2 text-sm font-medium text-gray-300">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 text-white placeholder-gray-400 transition-colors border rounded-lg bg-slate-700/50 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="What can we help you with?"
                />
              </div>

              <div>
                <label htmlFor="body" className="block mb-2 text-sm font-medium text-gray-300">
                  Message *
                </label>
                <textarea
                  id="body"
                  name="body"
                  value={formData.body}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 text-white placeholder-gray-400 transition-colors border rounded-lg resize-none bg-slate-700/50 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Please provide details about your inquiry, integration needs, or any questions you may have..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center justify-center w-full px-6 py-3 space-x-2 font-semibold text-white transition-colors bg-orange-500 rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                <span>{isSubmitting ? 'Processing...' : 'Send Message'}</span>
              </button>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div className="space-y-8" variants={fadeInRight}>
            <div className="p-8 border bg-slate-800/50 backdrop-blur-sm rounded-2xl border-slate-700/50">
              <h2 className="mb-6 text-2xl font-semibold text-white">Get in Touch</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-orange-500/20">
                    <Mail className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Email</h3>
                    <p className="text-gray-300">info@pexipay.com</p>
                    <p className="text-sm text-gray-400">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-orange-500/20">
                    <Phone className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Support</h3>
                    <p className="text-gray-300">Available 24/7</p>
                    <p className="text-sm text-gray-400">
                      For technical assistance and integration support
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-orange-500/20">
                    <MapPin className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Global Presence</h3>
                    <p className="text-gray-300">Worldwide Service</p>
                    <p className="text-sm text-gray-400">
                      Serving businesses across all time zones
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="p-8 border bg-slate-800/50 backdrop-blur-sm rounded-2xl border-slate-700/50">
              <h2 className="mb-6 text-2xl font-semibold text-white">Common Questions</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="mb-2 font-medium text-white">How long does integration take?</h3>
                  <p className="text-sm text-gray-300">
                    Most integrations can be completed within 24-48 hours with our comprehensive API
                    documentation.
                  </p>
                </div>
                <div>
                  <h3 className="mb-2 font-medium text-white">
                    What cryptocurrencies do you support?
                  </h3>
                  <p className="text-sm text-gray-300">
                    We support major cryptocurrencies including Bitcoin, Ethereum, and various
                    stablecoins.
                  </p>
                </div>
                <div>
                  <h3 className="mb-2 font-medium text-white">Is there a setup fee?</h3>
                  <p className="text-sm text-gray-300">
                    Yes there is a setup fees. You pay setup fees based on your processing volume.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Additional Info */}
        <motion.div className="mt-12 text-center" variants={fadeInUp}>
          <div className="inline-flex items-center px-6 py-3 border rounded-lg bg-orange-500/10 border-orange-500/20">
            <Mail className="w-5 h-5 mr-2 text-orange-400" />
            <span className="text-orange-300">
              For urgent matters, please email us directly at{' '}
              <a
                href="mailto:paul@pexipay.com"
                className="font-semibold transition-colors hover:text-orange-200"
              >
                paul@pexipay.com
              </a>
            </span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}
